/**
 * PlyxSellerOnboarding — verification + Stripe Connect onboarding for
 * Plyx creators who want to publish products in their shop.
 *
 * Routes: `/plyx/seller`
 *
 * Three states:
 *   • `none`     — no application yet, render the apply form
 *   • `pending`  — application is under review
 *   • `approved` — show Stripe Connect onboarding CTA + Connect status
 *   • `rejected` — show reason + allow re-apply
 *
 * Once seller_status is "approved" AND stripe.charges_enabled is true,
 * the creator can publish products in their Plyx Shop. We render a
 * direct link to /plyx/shop/manage when both are green.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import {
  Loader2, ShieldCheck, ShieldAlert, ArrowLeft, Upload, FileText,
  CheckCircle2, AlertCircle, ExternalLink, CreditCard, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { playPlyxChime } from "@/lib/plyxChime";

const STATUS_COPY = {
  none: { label: "Not applied yet", className: "bg-white/10 text-white" },
  pending: { label: "Pending review", className: "bg-[#FFB42D]/20 text-[#FFB42D]" },
  approved: { label: "Approved", className: "bg-white/10/20 text-white/85" },
  rejected: { label: "Rejected", className: "bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/25 text-[#FF2D7A]" },
};

const PlyxSellerOnboarding = () => {
  const { user, loading: authLoading } = usePlyxAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  const reload = () => {
    setLoading(true);
    plyxApi
      .get("/plyx/seller/status")
      .then((r) => setStatus(r.data))
      .catch(() => toast.error("Could not load seller status"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) return;
    reload();
  }, [user]);

  useEffect(() => {
    // Stripe OAuth round-trip lands here with ?stripe=connected. Refresh
    // the status block once so the creator sees the green ticks.
    if (searchParams.get("stripe") === "connected") {
      toast.success("Stripe account connected!");
      // Sound the Plyx chime — this is the "you can now earn" moment
      // and worth a celebration cue.
      playPlyxChime({ volume: 0.7 });
      plyxApi.get("/plyx/seller/connect/status").catch(() => {}).finally(reload);
    } else if (searchParams.get("stripe") === "error") {
      toast.error(`Stripe error: ${searchParams.get("reason") || "unknown"}`);
    }
  }, [searchParams]);

  if (!authLoading && !user) {
    return <Navigate to="/plyx/login?next=/plyx/seller" replace />;
  }

  if (loading || !status) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center" data-testid="plyx-seller-loading">
        <Loader2 className="w-6 h-6 animate-spin opacity-60"/>
      </div>
    );
  }

  const sellerStatus = status.seller_status || "none";
  const stripe = status.stripe || {};
  const application = status.application;
  const canPublish = status.can_publish_products && stripe.charges_enabled;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-seller-page">
      <div className="max-w-2xl mx-auto px-5 pt-6 pb-24">
        <Link to="/plyx/settings" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mb-4">
          <ArrowLeft className="w-4 h-4"/> back to settings
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: "#E85D4E" }}
          >
            <ShieldCheck className="w-6 h-6 text-black" strokeWidth={2.4}/>
          </div>
          <div>
            <h1 className="font-display text-3xl tracking-tight">Become a Plyx seller</h1>
            <p className="text-xs text-white/55 mt-0.5">
              Light verification + Stripe payouts. Takes 2 minutes.
            </p>
          </div>
        </div>

        {/* ── Status pill ── */}
        <div className="flex items-center gap-2 mb-6">
          <span
            data-testid="plyx-seller-status-pill"
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COPY[sellerStatus].className}`}
          >
            {sellerStatus === "approved" ? <CheckCircle2 className="w-3.5 h-3.5"/> :
              sellerStatus === "rejected" ? <AlertCircle className="w-3.5 h-3.5"/> :
              sellerStatus === "pending" ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> :
              <ShieldAlert className="w-3.5 h-3.5"/>}
            {STATUS_COPY[sellerStatus].label}
          </span>
          {canPublish && (
            <Link
              to="/plyx/shop/manage"
              data-testid="plyx-seller-manage-shop"
              className="ml-auto text-xs font-semibold text-[#FFB42D] hover:text-[#FFD37A] inline-flex items-center gap-1"
            >
              Manage shop <ExternalLink className="w-3 h-3"/>
            </Link>
          )}
        </div>

        {/* ── Application form / status ── */}
        {sellerStatus === "none" && (
          <ApplicationForm onSubmitted={reload}/>
        )}

        {sellerStatus === "pending" && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 mb-6" data-testid="plyx-seller-pending-card">
            <div className="font-semibold mb-1">We&apos;re reviewing your application</div>
            <p className="text-sm text-white/65">
              Our team typically reviews within 24 hours. We&apos;ll email you
              once approved. Submitted on {new Date(application.submitted_at).toLocaleString()}.
            </p>
            <ApplicationSummary app={application}/>
          </div>
        )}

        {sellerStatus === "rejected" && (
          <div className="rounded-2xl border border-[#FF2D7A]/30 bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/10 p-6 mb-6" data-testid="plyx-seller-rejected-card">
            <div className="font-semibold mb-1 text-[#FF8AB5]">Application not approved</div>
            <p className="text-sm text-white/75">
              <span className="font-semibold">Reason:</span> {application?.reject_reason || "—"}
            </p>
            <div className="mt-4">
              <ApplicationForm onSubmitted={reload} reapply/>
            </div>
          </div>
        )}

        {sellerStatus === "approved" && (
          <>
            <div className="rounded-2xl border border-[#00E0C7]/30 bg-white/10/10 p-6 mb-6" data-testid="plyx-seller-approved-card">
              <div className="font-semibold mb-1 text-[#7CF0DD] inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4"/> You&apos;re verified!
              </div>
              <p className="text-sm text-white/75">
                Next: connect Stripe to start receiving payouts on every Plyx sale.
              </p>
              <ApplicationSummary app={application}/>
            </div>

            <StripeConnectCard stripe={stripe} onChanged={reload}/>
          </>
        )}

        {/* ── Why we ask ── */}
        <div className="mt-8 rounded-2xl border border-white/8 bg-white/[0.03] p-5 text-xs text-white/55">
          <div className="font-semibold text-white/75 mb-2">Why we verify sellers</div>
          We&apos;re building Plyx into a marketplace buyers can trust. Light
          ID checks keep bad actors out, and Stripe handles every payout
          securely — we never see your bank details.
        </div>
      </div>
    </div>
  );
};

const ApplicationForm = ({ onSubmitted, reapply = false }) => {
  const [legalName, setLegalName] = useState("");
  const [country, setCountry] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [idDocUrl, setIdDocUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await plyxApi.post("/plyx/seller/upload-id", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIdDocUrl(data.url);
      toast.success("ID uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!legalName.trim() || !country.trim() || !idDocUrl) {
      toast.error("Fill every required field and upload your ID.");
      return;
    }
    setSubmitting(true);
    try {
      await plyxApi.post("/plyx/seller/apply", {
        legal_name: legalName.trim(),
        country: country.trim(),
        business_name: businessName.trim() || null,
        id_doc_url: idDocUrl,
      });
      toast.success("Application submitted");
      onSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-4" data-testid="plyx-seller-apply-form">
      <div className="font-semibold mb-2">
        {reapply ? "Submit a new application" : "Tell us who you are"}
      </div>

      <Field label="Legal name (as on your ID)*">
        <input
          type="text"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          data-testid="plyx-seller-legal-name"
          className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FFB42D]"
          maxLength={120}
          required
        />
      </Field>

      <Field label="Country*">
        <input
          type="text"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          data-testid="plyx-seller-country"
          className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FFB42D]"
          placeholder="e.g. Nigeria, United Kingdom, Ghana"
          maxLength={80}
          required
        />
      </Field>

      <Field label="Business name (optional)">
        <input
          type="text"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          data-testid="plyx-seller-business-name"
          className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FFB42D]"
          placeholder="Leave blank if you sell as an individual"
          maxLength={120}
        />
      </Field>

      <Field label="Government ID photo*">
        <div className="space-y-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={onPickFile}
            data-testid="plyx-seller-id-file"
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-full px-3 py-3 rounded-md border border-dashed border-white/25 hover:border-[#FFB42D] text-xs inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            {uploading
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Uploading…</>
              : idDocUrl
                ? <><CheckCircle2 className="w-3.5 h-3.5 text-white/85"/> Replace ID</>
                : <><Upload className="w-3.5 h-3.5"/> Upload passport / driving licence (JPG/PNG/PDF, ≤ 6MB)</>}
          </button>
          {idDocUrl && (
            <div className="text-[10px] text-white/50 inline-flex items-center gap-1">
              <FileText className="w-3 h-3"/> File ready — we&apos;ll review it manually, it&apos;s never public.
            </div>
          )}
        </div>
      </Field>

      <button
        type="submit"
        disabled={submitting}
        data-testid="plyx-seller-submit"
        className="w-full h-11 rounded-full font-semibold text-black disabled:opacity-50"
        style={{ background: "#E85D4E" }}
      >
        {submitting ? "Submitting…" : "Submit application"}
      </button>
    </form>
  );
};

const ApplicationSummary = ({ app }) => {
  if (!app) return null;
  return (
    <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
      <Pair label="Legal name" value={app.legal_name}/>
      <Pair label="Country" value={app.country}/>
      {app.business_name && <Pair label="Business" value={app.business_name}/>}
      <Pair label="Submitted" value={new Date(app.submitted_at).toLocaleDateString()}/>
    </div>
  );
};

const Pair = ({ label, value }) => (
  <div>
    <div className="text-white/45">{label}</div>
    <div className="text-white/85 font-medium truncate">{value}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-white/70 mb-1.5 block">{label}</span>
    {children}
  </label>
);

const StripeConnectCard = ({ stripe, onChanged }) => {
  const [connecting, setConnecting] = useState(false);

  const onConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await plyxApi.post("/plyx/seller/connect/start");
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Stripe Connect not available yet");
      setConnecting(false);
    }
  };

  const onRefresh = async () => {
    try {
      await plyxApi.get("/plyx/seller/connect/status");
      onChanged?.();
      toast.success("Stripe status refreshed");
    } catch (err) {
      toast.error("Couldn't refresh Stripe status");
    }
  };

  if (!stripe.connected) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="plyx-seller-stripe-card">
        <div className="flex items-center gap-3 mb-3">
          <CreditCard className="w-5 h-5 text-white/85"/>
          <div className="font-semibold">Connect Stripe to receive payouts</div>
        </div>
        <p className="text-sm text-white/65 mb-4">
          Plyx splits every sale via Stripe Connect: buyers pay, the platform
          takes its commission, the rest lands in your bank — automatically.
        </p>
        <button
          type="button"
          onClick={onConnect}
          disabled={connecting}
          data-testid="plyx-seller-stripe-connect"
          className="w-full h-11 rounded-full font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ background: "#635bff" }}
        >
          {connecting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Building2 className="w-4 h-4"/>}
          {connecting ? "Redirecting…" : "Connect Stripe account"}
        </button>
      </div>
    );
  }

  const ready = stripe.charges_enabled && stripe.payouts_enabled;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="plyx-seller-stripe-card">
      <div className="flex items-center gap-3 mb-3">
        <CreditCard className="w-5 h-5 text-white/85"/>
        <div className="font-semibold">Stripe connected</div>
        <span className={`ml-auto text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
          ready ? "bg-white/10/20 text-white/85" : "bg-[#FFB42D]/20 text-[#FFB42D]"
        }`}>
          {ready ? "Ready" : "Needs info"}
        </span>
      </div>
      <div className="space-y-1.5 text-xs text-white/70 mb-4">
        <Check label="Charges enabled" ok={stripe.charges_enabled}/>
        <Check label="Payouts enabled" ok={stripe.payouts_enabled}/>
        <Check label="Details submitted" ok={stripe.details_submitted}/>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        data-testid="plyx-seller-stripe-refresh"
        className="text-xs text-white/60 hover:text-white underline"
      >
        Refresh Stripe status
      </button>
    </div>
  );
};

const Check = ({ label, ok }) => (
  <div className="flex items-center gap-2">
    {ok
      ? <CheckCircle2 className="w-3.5 h-3.5 text-white/85"/>
      : <AlertCircle className="w-3.5 h-3.5 text-[#FFB42D]"/>}
    {label}
  </div>
);

export default PlyxSellerOnboarding;
